import React, { Component } from 'react';
import { toast } from 'react-toastify';

import * as api from '../api';
import {
    APPS,
    CONVERSATION_STATUSES,
    ESCALATE_REASON_LABELS,
    formatDateTime,
} from '../constants';

/** Dữ liệu lưu mã ứng dụng ("vi" / "en.yokara"); hiển thị tên app cho dễ đọc. */
const appLabel = (value) => (APPS.find((app) => app.value === value) || {}).label || value || '—';

const VIEW_PENDING = 'PENDING';
const VIEW_STATUS = 'STATUS';

const STATUS_META = {
    NEED_AGENT: { className: 'cs-badge cs-badge--danger', label: 'Chờ nhân viên', icon: 'fa-solid fa-circle-exclamation' },
    BOT_HANDLED: { className: 'cs-badge cs-badge--muted', label: 'Chatbot tự lo', icon: 'fa-solid fa-robot' },
    RESOLVED: { className: 'cs-badge cs-badge--success', label: 'Đã xử lý', icon: 'fa-solid fa-circle-check' },
};

const STAT_META = {
    NEED_AGENT: { icon: 'fa-solid fa-circle-exclamation', modifier: 'cs-stat--danger', hint: 'Cần người thật xem' },
    BOT_HANDLED: { icon: 'fa-solid fa-robot', modifier: '', hint: 'Chatbot tự trả lời được' },
    RESOLVED: { icon: 'fa-solid fa-circle-check', modifier: 'cs-stat--success', hint: 'Nhân viên đã đóng' },
};

/**
 * Danh sách hội thoại chatbot đã xử lý, để nhân viên biết chỗ nào cần xem.
 */
export default class ConversationTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            view: VIEW_PENDING,
            language: '',
            status: 'NEED_AGENT',
            conversations: [],
            cursor: null,
            loading: true,
            loadingMore: false,
            stats: null,
            detail: null,
            resolvingId: null,
            togglingBotReply: false,
        };
    }

    componentDidMount() {
        this.load();
        this.loadStats();
    }

    notifyChanged = () => {
        if (this.props.onDataChanged) {
            this.props.onDataChanged();
        }
    };

    buildParams = (cursor) => {
        const { view, language, status } = this.state;
        const params = { size: 50, cursor };

        if (view === VIEW_PENDING && language) {
            params.language = language;
        }
        if (view === VIEW_STATUS) {
            params.status = status;
        }
        return params;
    };

    callList = (cursor) => {
        const params = this.buildParams(cursor);
        if (this.state.view === VIEW_STATUS) return api.listConversation(params);
        return api.listPendingConversation(params);
    };

    load = () => {
        this.setState({ loading: true });
        this.callList(null)
            .then((page) => this.setState({
                conversations: (page && page.content) || [],
                cursor: page && page.cursor,
                loading: false,
            }))
            .catch(() => {
                this.setState({ conversations: [], cursor: null, loading: false });
                toast.error('Không tải được danh sách hội thoại.');
            });
    };

    /** Phân trang bằng con trỏ do backend trả về; "END" nghĩa là hết. */
    loadMore = () => {
        this.setState({ loadingMore: true });
        this.callList(this.state.cursor)
            .then((page) => this.setState((prev) => ({
                conversations: prev.conversations.concat((page && page.content) || []),
                cursor: page && page.cursor,
                loadingMore: false,
            })))
            .catch(() => {
                this.setState({ loadingMore: false });
                toast.error('Không tải thêm được.');
            });
    };

    loadStats = () => {
        api.statsConversation()
            .then((stats) => this.setState({ stats }))
            .catch(() => this.setState({ stats: null }));
    };

    reloadAll = () => {
        this.load();
        this.loadStats();
    };

    handleChangeView = (view) => {
        this.setState({ view }, this.load);
    };

    handleResolve = (conversation) => {
        if (!window.confirm(`Đánh dấu đã xử lý xong hội thoại với ${conversation.userName || conversation.userId}?`)) {
            return;
        }
        this.setState({ resolvingId: conversation.userId });
        api.resolveConversation(conversation.userId)
            .then(() => {
                this.setState({ resolvingId: null });
                toast.success('Đã đánh dấu xử lý xong.');
                this.reloadAll();
                this.notifyChanged();
            })
            .catch(() => {
                this.setState({ resolvingId: null });
                toast.error('Không cập nhật được trạng thái.');
            });
    };

    openDetail = (conversation) => {
        api.getConversation(conversation.userId)
            .then((detail) => this.setState({ detail }))
            .catch(() => toast.error('Không tải được chi tiết hội thoại.'));
    };

    handleToggleBotReply = () => {
        const { detail } = this.state;
        if (!detail) return;

        const isEnabled = detail.customerSupportBotEnableReply !== false;
        const action = isEnabled ? 'tắt' : 'bật';
        if (!window.confirm(
            `Bạn có muốn ${action} phản hồi tự động của BOT CSKH trong hội thoại này không?`
        )) {
            return;
        }

        this.setState({ togglingBotReply: true });
        api.toggleConversationBotReply(detail.userId, !isEnabled)
            .then((updated) => {
                this.setState({
                    detail: updated || { ...detail, customerSupportBotEnableReply: !isEnabled },
                    togglingBotReply: false,
                });
                toast.success(isEnabled
                    ? 'Đã tắt phản hồi tự động cho hội thoại này.'
                    : 'Đã bật lại phản hồi tự động cho hội thoại này.');
            })
            .catch(() => {
                this.setState({ togglingBotReply: false });
                toast.error('Không cập nhật được trạng thái phản hồi tự động.');
            });
    };

    renderBadge(status) {
        const meta = STATUS_META[status] || { className: 'cs-badge cs-badge--muted', label: status, icon: '' };
        return (
            <span className={meta.className}>
                {meta.icon && <i className={meta.icon} />}
                {meta.label}
            </span>
        );
    }

    renderStats() {
        const { stats } = this.state;
        if (!stats || !stats.counts) return null;

        return (
            <div className="cs-stats">
                {CONVERSATION_STATUSES.map((item) => {
                    const meta = STAT_META[item.value] || {};
                    return (
                        <div key={item.value} className={`cs-stat ${meta.modifier || ''}`}>
                            <div className="cs-stat__label">
                                {meta.icon && <i className={meta.icon} />}
                                {item.label}
                            </div>
                            <div className="cs-stat__value">{stats.counts[item.value] ?? 0}</div>
                            <div className="cs-stat__hint">{meta.hint}</div>
                        </div>
                    );
                })}

            </div>
        );
    }

    renderDetail() {
        const { detail, togglingBotReply } = this.state;
        if (!detail) return null;

        const isBotReplyEnabled = detail.customerSupportBotEnableReply !== false;

        const leftRows = [
            ['Mã người dùng', detail.userId],
            ['Ứng dụng', appLabel(detail.language)],
            ['Lý do chuyển gần nhất',
                ESCALATE_REASON_LABELS[detail.lastEscalateReason] || detail.lastEscalateReason || '—'],
            ['Số lượt chatbot tự trả lời', detail.totalBotReplies ?? 0],
            ['Số lần chuyển nhân viên', detail.totalEscalations ?? 0],
            ['Mã đoạn chat', detail.privateChatId || '—'],
        ];

        const rightRows = [
            ['Bắt đầu', formatDateTime(detail.firstMessageTime)],
            ['Người dùng nhắn lần cuối', formatDateTime(detail.lastUserMessageTime)],
            ['Cần nhân viên lúc', formatDateTime(detail.needAgentTime)],
            ['Chatbot trả lời lần cuối', formatDateTime(detail.lastBotReplyTime)],
            ['Đã xử lý lúc', formatDateTime(detail.resolvedTime)],
            ['Người xử lý', detail.resolvedBy || '—'],
        ];

        const renderRows = (rows) => rows.map(([label, value]) => (
            <div className="cs-detail-row" key={label}>
                <span className="cs-detail-row__label">{label}</span>
                <span className="cs-detail-row__value">{value}</span>
            </div>
        ));

        return (
            <div className="cs-card cs-card--accent">
                <div className="cs-card__head">
                    <div>
                        <h2>
                            {detail.userName || '(không rõ tên)'} {this.renderBadge(detail.status)}
                        </h2>
                        <div className="cs-card__desc">
                            Bản ghi theo dõi của chatbot. Nội dung hội thoại đầy đủ nằm trong hộp thư CSKH.
                        </div>
                    </div>
                    <div className="cs-actions">
                        {detail.status !== 'RESOLVED' && (
                            <button
                                type="button"
                                className="btn btn-success btn-sm"
                                onClick={() => this.handleResolve(detail)}
                            >
                                <i className="fa-solid fa-check mr-1" />
                                Đã xử lý
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => this.setState({ detail: null })}
                        >
                            Đóng
                        </button>
                    </div>
                </div>

                <div className="cs-card__body">
                    <div className={`cs-bot-reply-control ${isBotReplyEnabled ? 'is-enabled' : 'is-disabled'}`}>
                        <div>
                            <div className="cs-bot-reply-control__title">
                                <i className="fa-solid fa-robot" />
                                Phản hồi tự động của BOT CSKH
                            </div>
                            <div className="cs-bot-reply-control__desc">
                                {isBotReplyEnabled
                                    ? 'Đang bật cho riêng hội thoại này. Tắt để nhân viên tiếp quản hoàn toàn.'
                                    : 'Đã tắt cho riêng hội thoại này. Tin nhắn mới sẽ không kích hoạt BOT.'}
                            </div>
                        </div>
                        <button
                            type="button"
                            className={`btn btn-sm ${isBotReplyEnabled ? 'btn-outline-danger' : 'btn-outline-success'}`}
                            onClick={this.handleToggleBotReply}
                            disabled={togglingBotReply}
                        >
                            <i className={`fa-solid ${isBotReplyEnabled ? 'fa-pause' : 'fa-play'} mr-1`} />
                            {togglingBotReply
                                ? 'Đang cập nhật…'
                                : isBotReplyEnabled ? 'Tắt BOT tại hội thoại này' : 'Bật lại BOT'}
                        </button>
                    </div>

                    <div className="cs-detail-grid">
                        <div>{renderRows(leftRows)}</div>
                        <div>{renderRows(rightRows)}</div>
                    </div>

                    <div style={{ marginTop: '18px' }}>
                        <div className="cs-stat__label">
                            <i className="fa-solid fa-circle-question" />
                            Câu hỏi làm chatbot bí
                        </div>
                        <div className="cs-quote">{detail.needAgentMessage || '—'}</div>
                    </div>

                    <div className="cs-detail-grid" style={{ marginTop: '18px' }}>
                        <div>
                            <div className="cs-stat__label">
                                <i className="fa-solid fa-user" />
                                Tin cuối của người dùng
                            </div>
                            <div className="cs-quote">{detail.lastUserMessage || '—'}</div>
                        </div>
                        <div>
                            <div className="cs-stat__label">
                                <i className="fa-solid fa-robot" />
                                Tin cuối của chatbot
                            </div>
                            <div className="cs-quote">{detail.lastBotMessage || '—'}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderFilters() {
        const { view, language, status } = this.state;

        return (
            <div className="cs-card">
                <div className="cs-card__body">
                    <div className="cs-filters">
                        <div className="cs-field">
                            <label>Xem theo</label>
                            <select
                                className="form-control"
                                value={view}
                                onChange={(e) => this.handleChangeView(e.target.value)}
                            >
                                <option value={VIEW_PENDING}>Đang chờ nhân viên</option>
                                <option value={VIEW_STATUS}>Theo trạng thái</option>
                            </select>
                        </div>

                        {view === VIEW_PENDING && (
                            <div className="cs-field">
                                <label>Ứng dụng</label>
                                <select
                                    className="form-control"
                                    value={language}
                                    onChange={(e) => this.setState({ language: e.target.value }, this.load)}
                                >
                                    <option value="">Tất cả</option>
                                    {APPS.map((app) => (
                                        <option key={app.value} value={app.value}>{app.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {view === VIEW_STATUS && (
                            <div className="cs-field">
                                <label>Trạng thái</label>
                                <select
                                    className="form-control"
                                    value={status}
                                    onChange={(e) => this.setState({ status: e.target.value }, this.load)}
                                >
                                    {CONVERSATION_STATUSES.map((item) => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}


                        <div className="cs-filters__spacer" />

                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={this.reloadAll}
                        >
                            <i className="fa-solid fa-rotate mr-1" />
                            Tải lại
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderTable() {
        const { conversations, loading, resolvingId } = this.state;

        if (loading) {
            return <div className="cs-loading">Đang tải…</div>;
        }

        if (conversations.length === 0) {
            return (
                <div className="cs-empty">
                    <div className="cs-empty__icon"><i className="fa-solid fa-inbox" /></div>
                    <div className="cs-empty__title">Không có hội thoại nào</div>
                    <div className="cs-empty__desc">
                        Chưa có ai nhắn tới tài khoản CSKH trong phạm vi đang lọc, hoặc chatbot đã tự lo được hết.
                    </div>
                </div>
            );
        }

        return (
            <div className="cs-table-wrap">
                <table className="cs-table">
                    <thead>
                        <tr>
                            <th style={{ width: '20%' }}>Người dùng</th>
                            <th style={{ width: '12%' }}>Trạng thái</th>
                            <th style={{ width: '17%' }}>Lý do chuyển</th>
                            <th style={{ width: '26%' }}>Câu hỏi làm chatbot bí</th>
                            <th style={{ width: '13%' }}>Thời điểm</th>
                            <th style={{ width: '12%' }}>Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {conversations.map((item) => (
                            <tr key={item.userId}>
                                <td>
                                    <div className="cs-cell-strong">{item.userName || '(không rõ tên)'}</div>
                                    <div className="cs-cell-sub">{item.userId}</div>
                                </td>
                                <td>{this.renderBadge(item.status)}</td>
                                <td>
                                    {ESCALATE_REASON_LABELS[item.lastEscalateReason]
                                        || item.lastEscalateReason || '—'}
                                </td>
                                <td className="cs-cell-wrap">
                                    {item.needAgentMessage || item.lastUserMessage || '—'}
                                </td>
                                <td className="cs-cell-sub" style={{ marginTop: 0 }}>
                                    {formatDateTime(item.needAgentTime || item.lastUserMessageTime)}
                                </td>
                                <td>
                                    <div className="cs-actions">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => this.openDetail(item)}
                                        >
                                            Chi tiết
                                        </button>
                                        {item.status !== 'RESOLVED' && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-success"
                                                onClick={() => this.handleResolve(item)}
                                                disabled={resolvingId === item.userId}
                                            >
                                                Đã xử lý
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { conversations, loading, loadingMore, cursor } = this.state;
        const hasMore = cursor && cursor !== 'END';

        return (
            <div>
                {this.renderStats()}
                {this.renderFilters()}
                {this.renderDetail()}

                <div className="cs-card">
                    <div className="cs-card__head">
                        <div>
                            <h2>Danh sách hội thoại</h2>
                            <div className="cs-card__desc">
                                Mỗi người dùng một dòng. Bấm "Đã xử lý" khi đã trả lời xong — chatbot không
                                tự gỡ cờ chờ này.
                            </div>
                        </div>
                        {!loading && (
                            <span className="cs-badge cs-badge--info">
                                {conversations.length} hội thoại
                            </span>
                        )}
                    </div>

                    <div className="cs-card__body cs-card__body--flush">
                        {this.renderTable()}
                    </div>

                    {hasMore && (
                        <div className="cs-card__foot text-center">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={this.loadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? 'Đang tải…' : 'Tải thêm'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
